package com.dotdrives.web.pages.givers

import com.dotdrives.controller.GiversController
import com.dotdrives.csv.CsvRowResult
import com.dotdrives.csv.CsvTable
import com.dotdrives.csv.GiverCsv
import com.dotdrives.csv.GiverField
import com.dotdrives.design.DotButtonVariant
import com.dotdrives.design.DotTone
import com.dotdrives.model.NewGiver
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotButton
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.cancel
import kotlinx.coroutines.launch
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.style
import react.useEffect
import react.useState
import web.cssom.ClassName
import react.dom.aria.AriaRole
import web.dom.ElementId
import web.events.EventHandler
import web.events.ProgressEvent
import web.file.File
import web.file.FileReader
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.file as fileType

external interface ImportWizardProps : Props {
    var controller: GiversController
    var onClose: () -> Unit
    var onImported: (Int) -> Unit
}

private enum class DuplicateDecision {
    ImportAsNew,
    Skip,
}

val ImportWizard = FC<ImportWizardProps> { props ->
    com.dotdrives.web.ui.useDialogFocus(true, "giver-import-dialog", props.onClose)
    val (scope) = useState { MainScope() }
    val (step, setStep) = useState(1)
    val (fileName, setFileName) = useState("")
    val (table, setTable) = useState<CsvTable?>(null)
    val (mapping, setMapping) = useState<Map<String, GiverField?>>(emptyMap())
    val (results, setResults) = useState<List<CsvRowResult>>(emptyList())
    val (decisions, setDecisions) = useState<Map<Int, DuplicateDecision>>(emptyMap())
    val (loading, setLoading) = useState(false)
    val (error, setError) = useState<String?>(null)

    useEffect(scope) {
        try {
            awaitCancellation()
        } finally {
            scope.cancel()
        }
    }

    fun acceptFile(file: File) {
        setError(null)
        readCsvFile(
            file = file,
            onSuccess = { text ->
                runCatching { GiverCsv.parse(text) }
                    .onSuccess { parsed ->
                        if (parsed.headers.isEmpty()) {
                            setError("That CSV does not contain a header row.")
                        } else if (parsed.rows.isEmpty()) {
                            setError("That CSV does not contain any giver rows.")
                        } else {
                            setFileName(file.name)
                            setTable(parsed)
                            setMapping(GiverCsv.autoMap(parsed.headers))
                            setStep(2)
                        }
                    }
                    .onFailure { setError(it.message ?: "We could not read that CSV.") }
            },
            onFailure = { setError("We could not read that file.") },
        )
    }

    fun reviewRows() {
        val currentTable = table ?: return
        setLoading(true)
        setError(null)
        scope.launch {
            props.controller.listForCsvImport()
                .onSuccess { existing ->
                    val reviewed = GiverCsv.toGivers(currentTable, mapping, existing)
                    setResults(reviewed)
                    setDecisions(
                        reviewed.filterIsInstance<CsvRowResult.Duplicate>()
                            .associate { it.rowNumber to DuplicateDecision.Skip },
                    )
                    setStep(3)
                }
                .onFailure {
                    setError(it.message ?: "We could not compare this file with your directory.")
                }
            setLoading(false)
        }
    }

    fun importRows() {
        val givers = selectedImports(results, decisions)
        if (givers.isEmpty()) return
        setLoading(true)
        setError(null)
        scope.launch {
            props.controller.createMany(givers)
                .onSuccess(props.onImported)
                .onFailure { setError(it.message ?: "The import could not be completed.") }
            setLoading(false)
        }
    }

    ImportWizardStyles()
    div {
        className = ClassName("givers-backdrop givers-import-backdrop")
        role = "presentation".unsafeCast<AriaRole>()
        onMouseDown = { event ->
            if (event.target == event.currentTarget) props.onClose()
        }
        div {
            id = ElementId("giver-import-dialog")
            className = ClassName("givers-import-dialog")
            role = "dialog".unsafeCast<AriaRole>()
            ariaModal = true
            ariaLabel = "Import givers from CSV"

            div {
                className = ClassName("givers-import-head")
                div {
                    className = ClassName("givers-import-title")
                    +(if (step == 3) "Review & import" else "Import givers")
                }
                span {
                    className = ClassName("givers-import-meta")
                    +"STEP $step OF 3${fileName.takeIf(String::isNotBlank)?.let { " · $it" }.orEmpty()}"
                }
            }

            div {
                className = ClassName("givers-import-progress")
                repeat(3) { index ->
                    div {
                        className = ClassName(
                            "givers-import-progress-segment " +
                                if (index < step) "givers-import-progress-active" else "",
                        )
                    }
                }
            }

            error?.let { message ->
                DotBanner {
                    this.message = message
                    tone = DotTone.Danger
                    className = "givers-import-error"
                }
            }

            when (step) {
                1 -> ImportFileStep(::acceptFile, props.onClose)
                2 -> ImportMappingStep(
                    table = requireNotNull(table),
                    mapping = mapping,
                    onMapping = { header, field -> setMapping(mapping + (header to field)) },
                    onBack = { setStep(1) },
                    onReview = ::reviewRows,
                    loading = loading,
                )
                else -> ImportReviewStep(
                    results = results,
                    decisions = decisions,
                    onDecision = { row, decision -> setDecisions(decisions + (row to decision)) },
                    onBack = { setStep(2) },
                    onImport = ::importRows,
                    loading = loading,
                )
            }
        }
    }
}

private fun react.ChildrenBuilder.ImportFileStep(
    onFile: (File) -> Unit,
    onClose: () -> Unit,
) {
    div {
        className = ClassName("givers-import-copy")
        +"Choose the CSV you want to bring into your giver directory."
    }
    div {
        className = ClassName("givers-import-drop")
        onDragOver = { event -> event.preventDefault() }
        onDrop = { event ->
            event.preventDefault()
            event.dataTransfer.files.item(0)?.let(onFile)
        }
        div {
            className = ClassName("givers-import-file-icon")
            +"⇧"
        }
        div {
            className = ClassName("givers-import-drop-title")
            +"Drop your CSV here"
        }
        div {
            className = ClassName("givers-import-drop-copy")
            +"or browse for a .csv file"
        }
        input {
            id = "giver-csv-input".unsafeCast<ElementId>()
            type = InputType.fileType
            accept = ".csv,text/csv"
            className = ClassName("givers-import-file-input")
            onChange = { event ->
                event.currentTarget.files?.item(0)?.let(onFile)
                event.currentTarget.value = ""
            }
        }
        button {
            type = ButtonType.buttonType
            onClick = { kotlinx.browser.document.getElementById("giver-csv-input")?.asDynamic()?.click() }
            className = ClassName("dot-button dot-button-primary givers-import-browse")
            +"Browse files"
        }
    }
    div {
        className = ClassName("givers-dialog-actions")
        DotButton {
            text = "Cancel"
            onClick = onClose
            variant = DotButtonVariant.Secondary
        }
    }
}

private fun react.ChildrenBuilder.ImportMappingStep(
    table: CsvTable,
    mapping: Map<String, GiverField?>,
    onMapping: (String, GiverField?) -> Unit,
    onBack: () -> Unit,
    onReview: () -> Unit,
    loading: Boolean,
) {
    div {
        className = ClassName("givers-import-copy")
        +"We matched your columns automatically — fix anything that looks off."
    }
    div {
        className = ClassName("givers-import-mappings")
        table.headers.forEachIndexed { index, header ->
            div {
                key = "$index-$header".unsafeCast<Key>()
                className = ClassName("givers-import-map-row")
                div {
                    className = ClassName("givers-import-source")
                    div {
                        className = ClassName("givers-import-source-label")
                        +"YOUR COLUMN"
                    }
                    div {
                        className = ClassName("givers-import-source-value")
                        +header
                        span {
                            +" · \"${table.rows.firstOrNull()?.values?.getOrNull(index).orEmpty()}\""
                        }
                    }
                }
                div {
                    className = ClassName("givers-import-arrow")
                    +"→"
                }
                select {
                    className = ClassName("givers-import-select")
                    value = mapping[header]?.name.orEmpty()
                    ariaLabel = "Map $header"
                    onChange = { event ->
                        onMapping(
                            header,
                            GiverField.entries.firstOrNull { it.name == event.currentTarget.value },
                        )
                    }
                    option {
                        value = ""
                        +"Skip"
                    }
                    GiverField.entries.forEach { field ->
                        option {
                            key = field.name.unsafeCast<Key>()
                            value = field.name
                            +field.label
                        }
                    }
                }
            }
        }
    }
    div {
        className = ClassName("givers-import-actions")
        DotButton {
            text = "← Back"
            onClick = onBack
            variant = DotButtonVariant.Secondary
        }
        DotButton {
            text = "Review ${table.rows.size} rows →"
            onClick = onReview
            this.loading = loading
            className = "givers-import-primary"
        }
    }
}

private fun react.ChildrenBuilder.ImportReviewStep(
    results: List<CsvRowResult>,
    decisions: Map<Int, DuplicateDecision>,
    onDecision: (Int, DuplicateDecision) -> Unit,
    onBack: () -> Unit,
    onImport: () -> Unit,
    loading: Boolean,
) {
    val ready = results.filterIsInstance<CsvRowResult.Ready>()
    val duplicates = results.filterIsInstance<CsvRowResult.Duplicate>()
    val missing = results.filterIsInstance<CsvRowResult.MissingName>()
    val importCount = selectedImports(results, decisions).size

    div {
        className = ClassName("givers-import-counts")
        ImportCountCard(ready.size, "READY TO IMPORT", "ready")
        ImportCountCard(duplicates.size, "POSSIBLE DUPLICATES", "duplicates")
        ImportCountCard(missing.size, "MISSING NAME", "missing")
    }

    if (duplicates.isNotEmpty()) {
        div {
            className = ClassName("givers-import-dupe-label")
            +"POSSIBLE DUPLICATES — DECIDE EACH"
        }
        div {
            className = ClassName("givers-import-dupes")
            duplicates.forEach { duplicate ->
                val selected = decisions[duplicate.rowNumber] ?: DuplicateDecision.Skip
                div {
                    key = duplicate.rowNumber.toString().unsafeCast<Key>()
                    className = ClassName("givers-import-dupe")
                    div {
                        className = ClassName("givers-import-dupe-summary")
                        span {
                            className = ClassName("givers-import-dupe-name")
                            +duplicate.giver.displayName
                        }
                        span {
                            className = ClassName("givers-import-row-number")
                            +"CSV ROW ${duplicate.rowNumber}"
                        }
                        span {
                            className = ClassName("givers-import-matches")
                            +"matches"
                        }
                        span {
                            className = ClassName("givers-import-dupe-name")
                            +duplicate.existingDisplayName
                        }
                        span {
                            className = ClassName("givers-import-reason")
                            +duplicate.reason
                        }
                        span {
                            className = ClassName("givers-import-row-number")
                            +"DEFAULT: Skip row"
                        }
                    }
                    div {
                        className = ClassName("givers-import-decisions")
                        DecisionButton(
                            "Keep existing — skip row",
                            selected == DuplicateDecision.Skip,
                        ) { onDecision(duplicate.rowNumber, DuplicateDecision.Skip) }
                        DecisionButton(
                            "Import as new",
                            selected == DuplicateDecision.ImportAsNew,
                        ) { onDecision(duplicate.rowNumber, DuplicateDecision.ImportAsNew) }
                    }
                }
            }
        }
    }

    if (missing.isNotEmpty()) {
        div {
            className = ClassName("givers-import-missing-note")
            +"${missing.size} row${if (missing.size == 1) "" else "s"} without a name will be skipped."
        }
    }

    div {
        className = ClassName("givers-import-actions")
        DotButton {
            text = "← Back"
            onClick = onBack
            variant = DotButtonVariant.Secondary
        }
        DotButton {
            text = "Import $importCount givers ✓"
            onClick = onImport
            enabled = importCount > 0
            this.loading = loading
            className = "givers-import-primary"
        }
    }
}

private fun react.ChildrenBuilder.ImportCountCard(
    count: Int,
    label: String,
    tone: String,
) {
    div {
        className = ClassName("givers-import-count givers-import-count-$tone")
        div {
            className = ClassName("givers-import-count-value")
            +count.toString()
        }
        div {
            className = ClassName("givers-import-count-label")
            +label
        }
    }
}

private fun react.ChildrenBuilder.DecisionButton(
    text: String,
    selected: Boolean,
    onClick: () -> Unit,
) {
    button {
        type = ButtonType.buttonType
        className = ClassName(
            "givers-import-decision" + if (selected) " givers-import-decision-active" else "",
        )
        this.onClick = { onClick() }
        +text
    }
}

private fun selectedImports(
    results: List<CsvRowResult>,
    decisions: Map<Int, DuplicateDecision>,
): List<NewGiver> =
    results.mapNotNull { result ->
        when (result) {
            is CsvRowResult.Ready -> result.giver
            is CsvRowResult.Duplicate ->
                result.giver.takeIf {
                    decisions[result.rowNumber] == DuplicateDecision.ImportAsNew
                }
            is CsvRowResult.MissingName -> null
        }
    }

private val GiverField.label: String
    get() = when (this) {
        GiverField.FirstName -> "First name"
        GiverField.LastName -> "Last name"
        GiverField.DisplayName -> "Display name"
        GiverField.SpouseName -> "Spouse / partner"
        GiverField.Email -> "Email"
        GiverField.Phone -> "Phone"
        GiverField.Address -> "Address"
        GiverField.Tags -> "Tags"
    }

@OptIn(ExperimentalWasmJsInterop::class)
private fun readCsvFile(
    file: File,
    onSuccess: (String) -> Unit,
    onFailure: () -> Unit,
) {
    val reader = FileReader()
    reader.onload = { _: ProgressEvent ->
        onSuccess(reader.result.unsafeCast<String?>().orEmpty())
    }.unsafeCast<EventHandler<ProgressEvent, FileReader, FileReader>>()
    reader.onerror = { _: ProgressEvent ->
        onFailure()
    }.unsafeCast<EventHandler<ProgressEvent, FileReader, FileReader>>()
    reader.readAsText(file)
}

fun downloadGiverCsvTemplate() {
    js(
        """{
          const csv = 'name,email,phone,address\r\nAlex Rivera,alex@example.org,555-0100,"12 Oak Lane, Springfield"';
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'dot-drives-givers-template.csv';
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(url);
        }""",
    )
}

private val importWizardStyles = """
    .givers-import-backdrop { position:fixed;inset:0;display:flex;background:rgba(34,45,50,.34); z-index:100; align-items: center; justify-content: center; padding: 24px; }
    .givers-import-dialog {
      width: min(640px, 100%); max-height: calc(100vh - 48px); overflow: auto; padding: 26px 30px;
      border-radius: 20px; background: white; box-shadow: 0 24px 80px rgba(34,45,50,.25);
      animation: givers-pop-in 220ms ease both;
    }
    .givers-import-dialog .givers-dialog-actions {display:flex;justify-content:flex-end;margin-top:18px;}
    .givers-import-head { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
    .givers-import-title { font-family: 'Quicksand', sans-serif; font-size: 20px; font-weight: 700; }
    .givers-import-meta { color: #88949C; font-family: 'IBM Plex Mono', monospace; font-size: 10px; }
    .givers-import-progress { display: flex; gap: 5px; margin: 14px 0 18px; }
    .givers-import-progress-segment { height: 4px; flex: 1; border-radius: 2px; background: #EFF1F4; }
    .givers-import-progress-active { background: #F26223; }
    .givers-import-copy { margin-bottom: 16px; color: #88949C; font-size: 12.5px; }
    .givers-import-error { margin-bottom: 16px; }
    .givers-import-drop {
      padding: 42px 24px; border: 2px dashed #D6DBE0; border-radius: 16px; background: #F7F8FA;
      text-align: center; transition: border-color 140ms ease, background-color 140ms ease;
    }
    .givers-import-drop:hover { border-color: #F26223; background: #FEF8F6; }
    .givers-import-file-icon { color: #F26223; font-size: 34px; font-weight: 900; }
    .givers-import-drop-title { margin-top: 8px; font-family: 'Quicksand', sans-serif; font-size: 18px; font-weight: 700; }
    .givers-import-drop-copy { margin: 4px 0 16px; color: #88949C; font-size: 12px; }
    .givers-import-file-input { display: none; }
    .givers-import-browse { min-height: 40px; padding: 9px 18px; font-size: 12.5px; }
    .givers-import-mappings { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
    .givers-import-map-row { flex-shrink:0; display: grid; grid-template-columns: 1fr 28px 1fr; align-items: center; gap: 8px; }
    .givers-import-source { min-width: 0; padding: 9px 13px; border: 1px solid #E4E8EC; border-radius: 10px; background: #F7F8FA; }
    .givers-import-source-label { color: #88949C; font-family: 'IBM Plex Mono', monospace; font-size: 9px; }
    .givers-import-source-value { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12.5px; font-weight: 800; }
    .givers-import-source-value span { color: #B4BCC2; font-size: 11px; font-weight: 600; }
    .givers-import-arrow { pointer-events:none; color: #F26223; text-align: center; font-weight: 900; }
    .givers-import-select {
      width: 100%; min-width: 0; padding: 9px 13px; border: 1.5px solid #F26223; border-radius: 10px;
      outline: none; color: #B8491A; background: #FDE5DA; font-size: 12.5px; font-weight: 800;
    }
    .givers-import-actions { display: grid; grid-template-columns: 1fr 2fr; gap: 10px; margin-top: 18px; }
    .givers-import-primary { width: 100%; }
    .givers-import-counts { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 16px; }
    .givers-import-count { padding: 11px 14px; border-radius: 11px; }
    .givers-import-count-ready { color: #007942; background: #E3F1E8; }
    .givers-import-count-duplicates { color: #9A6700; background: #FFF6E5; }
    .givers-import-count-missing { color: #C22C25; background: #FDECEA; }
    .givers-import-count-value { font-family: 'Quicksand', sans-serif; font-size: 20px; font-weight: 700; }
    .givers-import-count-label { font-size: 10.5px; font-weight: 800; }
    .givers-import-dupe-label {
      margin-bottom: 8px; color: #9A6700; font-family: 'IBM Plex Mono', monospace;
      font-size: 10px; letter-spacing: 1px;
    }
    .givers-import-dupes { max-height: 300px; display: flex; flex-direction: column; gap: 8px; overflow: auto; }
    .givers-import-dupe { padding: 12px 14px; border: 1px solid #F2E3BC; border-radius: 12px; background: #FFFBF0; }
    .givers-import-dupe-summary { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 8px; }
    .givers-import-dupe-name { font-size: 13px; font-weight: 800; }
    .givers-import-row-number { color: #9A6700; font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; }
    .givers-import-matches { color: #88949C; font-size: 11px; }
    .givers-import-reason { padding: 2px 8px; border-radius: 99px; color: #5A6870; background: #EFF1F4; font-size: 10px; font-weight: 800; }
    .givers-import-decisions { display: flex; flex-wrap: wrap; gap: 7px; }
    .givers-import-decision {
      padding: 5px 12px; border: 1.5px solid #D6DBE0; border-radius: 99px; color: #5A6870;
      background: white; cursor: pointer; font-size: 11px; font-weight: 800;
    }
    .givers-import-decision-active { border-color: #222D32; color: white; background: #222D32; }
    .givers-import-missing-note { margin-top: 12px; color: #C22C25; font-size: 12px; }
    .givers-template-link {
      margin-top: 14px; color: #88949C; background: transparent; cursor: pointer; font-size: 12px;
    }
    .givers-template-link strong { color: #5A6870; }
    @media (max-width: 620px) {
      .givers-import-dialog { padding: 22px 18px; }
      .givers-import-head { align-items: flex-start; flex-direction: column; }
      .givers-import-map-row { grid-template-columns: 1fr; }
      .givers-import-arrow { transform:none;font-size:0;line-height:16px; }
      .givers-import-arrow::after { content:"↓";font-size:14px; }
      .givers-import-counts { grid-template-columns: 1fr; }
      .givers-import-actions { grid-template-columns: 1fr; }
    }
""".trimIndent()

private val ImportWizardStyles = FC<Props> {
    style { +importWizardStyles }
}
